import {
  OrderInternalDocumentViewModel,
  OrderInternalInputModel,
  OrderSuppliersDocumentViewModel,
  OrderSuppliersInputModel,
} from '@ail/orders-api'
import { CustomerDocumentWorkflowInputModel, DocumentKind, WorkflowKind, WorkflowStatus } from '@ail/workflows-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../auth/UseAuth'
import { Environment } from '../../../environment'
import { FileHelper as FH } from '../../../helpers/FileHelper'
import { parseFileData } from '../../../helpers/Utils'
import { useSearchContractsQuery } from '../../../redux/api/contractsApi'
import {
  isSupplierOrder,
  useLazyGetOrderInternalDocumentQuery,
  useLazyGetOrderSuppliersDocumentQuery,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../../redux/api/ordersApi'
import {
  useCreateDocumentMutation,
  useGetWorkflowQuery,
  useStartWorkflowMutation,
  useUploadDocumentMutation,
} from '../../../redux/api/workflowsApi'
import { Button } from '../../common/Buttons/Button'
import ContractCard from '../../common/ContractCard/ContractCard'
import { Dialog } from '../../common/Dialog/Dialog'
import { DocPreviewData } from '../DocumentsModal/DocumentsModal'
import './WorkflowPreviewModal.scss'
import { NoPreviewAvailable } from './NoPreviewAvailable'
import BoxTitle from '../../common/Box/BoxTitle'
import { usePopup } from '../../common/Popup/PopupContext'
import { PopupType } from '../../common/Popup/Popup'
import { Loader } from '../../common/Loader/Loader'
interface Props {
  data: DocPreviewData
  onClose: () => void
  onCancel: () => void
}
export interface FileData {
  fileURL: string
  filename: string
}
export function WorkflowPreviewModal({ data, onClose, onCancel }: Props) {
  const { document, order } = data
  const { t } = useTranslation()
  const { user } = useAuth()
  const contractReference = data.document.contractReference
    ? order.form.fields.find((f) => f.name === document.contractReference)?.value
    : order.contractReference

  const contracts = useSearchContractsQuery(
    { searchTerm: contractReference!, activeOnly: false },
    { skip: !contractReference }
  )
  const contract = contracts.currentData?.length === 1 ? contracts.currentData[0] : undefined
  const [createDocument] = useCreateDocumentMutation()
  const [uploadDocument] = useUploadDocumentMutation()
  const [startWorkflow] = useStartWorkflowMutation()
  const workflow = useGetWorkflowQuery(data.document.workflowId ?? skipToken)

  const [fileData, setFileData] = useState<FileData>()
  const [canPreview, setCanPreview] = useState(false)
  const [downloadInternal, dlIntRes] = useLazyGetOrderInternalDocumentQuery()
  const [downloadSuppliers, dlSupRes] = useLazyGetOrderSuppliersDocumentQuery()
  const [updateOrderInternal] = useUpdateOrderInternalMutation()
  const [updateOrderSuppliers] = useUpdateOrderSuppliersMutation()

  const blocking = dlIntRes.isFetching || dlSupRes.isFetching || contracts.isFetching

  const [uploading, setUploading] = useState(false)
  const [uploadCompleted, setUploadCompleted] = useState(false)
  const popup = usePopup()

  const pushPopup = useMemo(() => popup?.pushPopup, [popup?.pushPopup])

  useEffect(() => {
    async function getFile() {
      try {
        let result: OrderInternalDocumentViewModel | OrderSuppliersDocumentViewModel | undefined = undefined
        if (isSupplierOrder(order)) {
          result = await downloadSuppliers({ id: order.id, documentId: document.documentId! }, true).unwrap()
        } else {
          result = await downloadInternal({ id: order.id, documentId: document.documentId! }, true).unwrap()
        }

        const data = parseFileData(result.content)
        if (data) {
          setFileData(data)

          if (FH.checkFileType(data.fileURL) !== 'invalid') {
            setCanPreview(true)
          }
        }
      } catch (error) {
        console.log('error', error)
      }
    }
    getFile()
  }, [document.documentId, downloadInternal, downloadSuppliers, order])

  const handleStartWorkflow = useCallback(async () => {
    if (!fileData || !contract) return

    const model: OrderInternalInputModel | OrderSuppliersInputModel = {
      form: {
        status: data.order.form.status,
        fields: [
          {
            name: data.field.name,
            documents: [],
          },
        ],
      },
    }

    const description =
      document.workflowKind === WorkflowKind.CustomerDocument
        ? `${contract.reference} - Customer's document`
        : document.workflowKind

    const blob = await (await fetch(fileData.fileURL)).blob()
    const aiiDocument = await createDocument({
      kind: document.kind as DocumentKind,
      description: description ?? document.name,
    }).unwrap()

    await uploadDocument({ documentId: aiiDocument.id, file: { fileName: fileData.filename, data: blob } })

    let tags = ['ilink']
    const userTeams = user?.roles.map((r) => r.split('@')).filter((r) => r.length === 2) ?? []
    tags = [...tags, ...userTeams.map((r) => r[1])]

    const workflowModel: CustomerDocumentWorkflowInputModel = {
      kind: document.workflowKind!,
      description,
      documentId: aiiDocument.id,
      contractId: contract.id,
      tags,
    }

    const workflowLocation = await startWorkflow({ model: workflowModel }).unwrap()
    const workflowId = workflowLocation?.split('/').at(-1)
    model.form.fields[0].documents.push({ name: document.name, workflowId })

    if (!model.form.fields[0].documents.length) {
      return
    }

    if (isSupplierOrder(data.order)) {
      await updateOrderSuppliers({ id: data.order.id, model }).unwrap()
    } else {
      await updateOrderInternal({ id: data.order.id, model }).unwrap()
    }
  }, [
    contract,
    createDocument,
    data,
    document,
    fileData,
    startWorkflow,
    updateOrderInternal,
    updateOrderSuppliers,
    uploadDocument,
    user,
  ])

  const workflowBtnData = useMemo(() => {
    const hasWorkflowId = !!document.workflowId
    const statusLoading = hasWorkflowId && workflow.isFetching
    // The workflow exists, but its status could not be fetched (workflows api unresponsive).
    const statusUnavailable = hasWorkflowId && !workflow.isFetching && !workflow.data
    const status = !hasWorkflowId
      ? 'canStart'
      : statusLoading
        ? 'statusLoading'
        : statusUnavailable
          ? 'statusUnavailable'
          : workflow.data?.workflow.status === WorkflowStatus.Complete && workflow.data.workflow.hasErrors
            ? 'hasError'
            : (workflow.data?.workflow.status ?? '')
    // A terminated workflow can be started again for the same document.
    const canRestart = hasWorkflowId && workflow.data?.workflow.status === WorkflowStatus.Terminated
    const btnText = uploadCompleted
      ? 'WORKFLOW_STARTED'
      : hasWorkflowId
        ? statusLoading
          ? 'LOADING'
          : statusUnavailable
            ? 'WORKFLOWUNAVAILABLE'
            : canRestart
              ? 'RESTART_WORKFLOW'
              : (workflow.data?.workflow.status.toUpperCase() ?? '')
        : 'START_WORKFLOW'
    const navUrl = `${Environment.AiDocsUrl}/workflows/${document.workflowId}`
    const onClick = async () => {
      if (uploadCompleted || (hasWorkflowId && !canRestart)) return
      setUploading(true)
      try {
        await handleStartWorkflow()
        setUploadCompleted(true)
        setTimeout(onClose, 2000)
        pushPopup?.(t('WORKFLOW_STARTED'), PopupType.Success)
      } catch {
        setUploadCompleted(false)
        pushPopup?.(t('SOMETHING_WENT_WRONG'), PopupType.Error)
      } finally {
        setUploading(false)
      }
    }
    const openAii = () => {
      if (hasWorkflowId) {
        window.open(navUrl, '_blank', 'noopener,noreferrer')
        return
      }
    }
    return {
      status,
      btnText,
      navUrl,
      onClick,
      openAii,
    }
  }, [
    document.workflowId,
    workflow.data,
    workflow.isFetching,
    uploadCompleted,
    handleStartWorkflow,
    onClose,
    pushPopup,
    t,
  ])

  return (
    <Dialog open={true} onClose={onClose} class="docPreview">
      <Loader
        blocking={blocking}
        renderChildren={false}
        style={blocking ? { minWidth: '300px', minHeight: '100px' } : undefined}
        className="documents-modal"
      >
        <div className="documents-modal">
          <div className="documents-modal-content ">
            <div className="previewContainer">
              <BoxTitle title={document.kind ?? 'Preview'} color="azure" />
              {fileData &&
                (canPreview ? (
                  FH.isFileType(fileData.fileURL, 'image') ? (
                    <img src={fileData.fileURL} alt={fileData.filename} className="imgPreview" />
                  ) : (
                    <object data={fileData.fileURL} className="pdfPreview">
                      {!!document.documentId && <NoPreviewAvailable document={document} fileData={fileData} />}
                    </object>
                  )
                ) : (
                  !!document.documentId && <NoPreviewAvailable document={document} fileData={fileData} />
                ))}
            </div>
            <div className="infoContainer">
              <div>
                <ContractCard contract={contract} costCenter={order.costCenterName} companyName={order.companyName} />
              </div>
              <div className="documents-modal-actions">
                <button onClick={onCancel} className="transparent-button negative">
                  <i className="icon-close"></i>
                  {t('CANCEL')}
                </button>
                <div className="flex">
                  {/* {document.workflowId && <Button onClick={workflowBtnData.openAii} className="icon-aidocs" />} */}
                  <Button
                    onClick={workflowBtnData.onClick}
                    text={uploading ? t('STARTING') : t(workflowBtnData.btnText)}
                    icon={`icon ${workflowBtnData.status} `}
                    className={`previewButton transparent-button ${workflowBtnData.status} `}
                    disabled={uploading}
                  ></Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Loader>
    </Dialog>
  )
}
